<template>
  <div class="ca-hist-table">
    <table>
      <thead>
        <tr>
          <th
            v-for="column in columns"
            :key="column.id"
            :class="{ 'is-person': column.id === 'person', 'is-sortable': !!column.sorts, 'is-sorted': isSorted(column) }"
            :title="column.tip ? $t(column.tip) : null"
            :aria-sort="ariaSort(column)"
          >
            <button
              v-if="column.sorts"
              type="button"
              class="ca-hist-table__sort"
              @click="toggleSort(column)"
            >
              {{ $t(column.label) }}
              <span class="ca-hist-table__arrow" aria-hidden="true">{{ arrow(column) }}</span>
            </button>
            <template v-else>{{ $t(column.label) }}</template>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="person in people"
          :key="person.key"
          class="ca-hist-table__row"
          :class="{ 'is-selected': person.key === selectedKey, 'is-idle': !person.totals.daysWorked }"
          tabindex="0"
          @click="$emit('select', person.key)"
          @keydown.enter.prevent="$emit('select', person.key)"
          @keydown.space.prevent="$emit('select', person.key)"
        >
          <td class="is-person">
            <div class="ca-hist-table__name">
              <span
                v-if="person.totals.isClockedInNow"
                class="ca-hist-table__live-dot"
                :title="$t('ca_hist_clocked_in_now')"
              ></span>
              {{ person.username || $t('ca_hist_unknown_name') }}
            </div>
            <div class="ca-hist-table__meta">
              <span class="ca-hist-table__days">{{ $t('ca_hist_kpi_days') }}: {{ person.totals.daysWorked }}</span>
              <span v-if="person.phone">{{ person.phone }}</span>
              <span v-if="!person.isAssigned" class="ca-hist-table__badge">{{ $t('ca_hist_not_assigned') }}</span>
              <span v-else-if="!person.shiftIds.length" class="ca-hist-table__badge">{{ $t('ca_hist_no_shift') }}</span>
            </div>
          </td>
          <td>{{ formatMinutes(person.totals.grossMinutes) }}</td>
          <td class="ca-hist-table__strong">{{ formatMinutes(person.totals.netMinutes) }}</td>
          <td>{{ formatMinutes(person.totals.breakMinutes) }}</td>
          <td>
            <div class="ca-hist-table__issues">
              <span v-if="person.totals.lateDays > 0" class="ca-hist-table__count is-late">
                {{ person.totals.lateDays }} {{ $t('ca_hist_col_late') }}
              </span>
              <span v-if="person.totals.missedDays > 0" class="ca-hist-table__count is-missed">
                {{ person.totals.missedDays }} {{ $t('ca_hist_col_missed') }}
              </span>
              <span v-if="person.totals.missingClockOuts > 0" class="ca-hist-table__count is-warning">
                {{ person.totals.missingClockOuts }} {{ $t('ca_hist_col_no_clock_out') }}
              </span>
              <span v-if="!hasIssues(person)" class="ca-hist-table__none">—</span>
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import { formatMinutes } from '../cleanerActivityUtils'

export default {
  props: {
    people: { type: Array, required: true },
    selectedKey: { type: String, default: null },
    sortKey: { type: String, required: true },
  },
  data() {
    // `sorts` lists the sort keys a column cycles through on click (first = first click).
    return {
      columns: [
        { id: 'person', label: 'ca_hist_col_person', sorts: ['name'] },
        { id: 'total', label: 'ca_hist_col_total', tip: 'ca_hist_col_total_tip' },
        { id: 'net', label: 'ca_hist_col_net', sorts: ['most_hours', 'least_hours'] },
        { id: 'breaks', label: 'ca_hist_col_breaks' },
        { id: 'issues', label: 'ca_hist_col_issues', sorts: ['most_issues'] },
      ],
    }
  },
  methods: {
    formatMinutes,
    hasIssues(person) {
      const totals = person.totals
      return totals.lateDays > 0 || totals.missedDays > 0 || totals.missingClockOuts > 0
    },
    isSorted(column) {
      return !!column.sorts && column.sorts.includes(this.sortKey)
    },
    ariaSort(column) {
      if (!this.isSorted(column)) return column.sorts ? 'none' : null
      return this.sortKey === 'name' || this.sortKey === 'least_hours' ? 'ascending' : 'descending'
    },
    arrow(column) {
      if (!this.isSorted(column)) return ''
      return this.sortKey === 'name' || this.sortKey === 'least_hours' ? '▲' : '▼'
    },
    toggleSort(column) {
      const index = column.sorts.indexOf(this.sortKey)
      // Cycle through the column's keys; after the last one (or a single-key column) go back to the default.
      const next = index === -1 ? column.sorts[0] : column.sorts[index + 1] || 'name'
      this.$emit('sort', next)
    },
  },
}
</script>

<style lang="scss" scoped>
@use "@/styles/mixins" as *;

.ca-hist-table {
  overflow-x: auto;
  background: #fff;
  border: 1px solid #eef0f3;
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.84rem;
    color: #2c3e50;
  }

  th {
    position: sticky;
    top: 0;
    z-index: 1;
    padding: 10px 12px;
    background: #f8f9fb;
    border-bottom: 1px solid #eef0f3;
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #7f8c8d;
    text-align: start;
    white-space: nowrap;
  }

  td {
    padding: 10px 12px;
    border-bottom: 1px solid #f2f4f7;
    vertical-align: middle;
    white-space: nowrap;
  }

  .is-person {
    min-width: 200px;
    white-space: normal;
  }

  &__row {
    cursor: pointer;
    transition: background 0.12s ease;

    &:hover {
      background: #f7f9fb;
    }

    &:focus-visible {
      outline: 2px solid rgba(47, 128, 235, 0.45);
      outline-offset: -2px;
    }

    &.is-selected {
      background: #eef5ea;
    }

    &.is-idle td {
      color: #9aa5b1;
    }

    &:last-child td {
      border-bottom: none;
    }
  }

  &__sort {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 0;
    border: none;
    background: transparent;
    font: inherit;
    letter-spacing: inherit;
    text-transform: inherit;
    color: inherit;
    cursor: pointer;

    &:hover {
      color: #2c3e50;
    }
  }

  th.is-sorted {
    color: #2c3e50;
  }

  &__arrow {
    font-size: 0.6rem;
  }

  &__issues {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
  }

  &__none {
    color: #c4ccd4;
  }

  &__name {
    display: flex;
    align-items: center;
    gap: 6px;
    font-weight: 600;
  }

  &__live-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: $ca-clock-in;
    box-shadow: 0 0 0 2px rgba(47, 143, 78, 0.2);
    flex-shrink: 0;
  }

  &__days {
    flex: 1 0 100%;
    color: #7f8c8d;
  }

  &__meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    margin-top: 2px;
    font-size: 0.74rem;
    color: #9aa5b1;
  }

  &__badge {
    padding: 1px 6px;
    border-radius: 999px;
    background: #f1f3f6;
    color: #6b7785;
    font-size: 0.68rem;
    font-weight: 600;
  }

  &__strong {
    font-weight: 700;
  }

  &__sub {
    display: block;
    font-size: 0.72rem;
    color: #9aa5b1;
  }

  &__count {
    display: inline-block;
    min-width: 22px;
    padding: 1px 7px;
    border-radius: 999px;
    font-weight: 700;
    text-align: center;

    &.is-late {
      background: rgba(230, 126, 34, 0.12);
      color: #a85a12;
    }

    &.is-missed {
      background: #fdecea;
      color: #922b21;
    }

    &.is-warning {
      background: #fff4e5;
      color: #9a5b0a;
    }
  }
}
</style>
